import React from 'react';
import {
  Button,
  Checkbox,
  Grid,
  Link,
  TextField,
  Typography
} from '@mui/material';
import { DateField } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import _ from 'lodash';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';

interface IsignUpForm {
  first: string;
  last: string;
  dob: string;
  terms: boolean;
}

const defaultForm: IsignUpForm = {
  first: '',
  last: '',
  dob: '',
  terms: false
};

const formErrors: Record<string, boolean> = {
  first: false,
  last: false,
  dob: false
};

export function SignUp() {
  const {signUp, user} = useAuthContext();
  const [signUpForm, setSignUpForm] = React.useState(defaultForm);
  const [errors, setErrors] = React.useState(formErrors);

  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const currentDate = new Date(); // Get the current date

  function parseDateString(value: string, orginalValue: string) {
    const parsedDateTime = DateTime.fromFormat(
      orginalValue,
      'MM/dd/yyyy'
    ).toJSDate();
    if (currentDate < parsedDateTime) return 'Invalid Date';
    return parsedDateTime;
  }

  const validationSchema = Yup.object({
    first: Yup.string().required(),
    last: Yup.string().required(),
    dob: Yup.date().transform(parseDateString).required(),
    terms: Yup.boolean().oneOf([true]).required()
  });

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setSignUpForm((prevState: IsignUpForm) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setSignUpForm((prevState: IsignUpForm) => ({
      ...prevState,
      terms: isChecked
    }));
  };

  const validateField = async (field: string) => {
    try {
      await validationSchema.validateAt(field, signUpForm);
      const isError = false;
      setErrors((prevState: any) => ({ ...prevState, [field]: isError }));
    } catch (e: any) {
      const isError = true;
      setErrors((prevState: any) => ({ ...prevState, [field]: isError }));
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date && date instanceof Date && _.isFinite(date.getTime())) {
      const dateString = format(date, 'MM/dd/yyyy');
      setSignUpForm((prevState: IsignUpForm) => ({
        ...prevState,
        dob: dateString
      }));
    }
    validateField('dob');
  };

  const submitForm = async () => {
    try {
      await validationSchema.validate(signUpForm, { abortEarly: false });

      await signUp(user?.firebaseUser!, signUpForm.first, signUpForm.last, signUpForm.dob)

      // redirect
      router.replace(returnTo || paths.root);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e.errors); // TODO: log to sentry
    }
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ height: '100vh' }}
    >
      <Grid
        container
        item
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        md={4}
        xs={11}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '5px',
          margin: '0px',
          padding: '15px'
        }}
      >
        <Grid item xs={12}>
          <Typography variant="h2" textAlign="center">
            Create Account
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="first"
            id="first"
            label="First Name"
            error={errors.first}
            helperText={errors.first && 'First Name is required'}
            onBlur={() => validateField('first')}
            onChange={handleFormChange}
            value={signUpForm.first}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="last"
            id="last"
            label="Last Name"
            onBlur={() => validateField('last')}
            error={errors.last}
            helperText={errors.last && 'Last Name is required'}
            onChange={handleFormChange}
            value={signUpForm.last}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <DateField
            fullWidth
            label="Birthday"
            name="dob"
            id="dob"
            onBlur={() => validateField('dob')}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                error: errors.dob,
                helperText: errors.dob && 'Invalid Birthdate'
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography paragraph style={{ margin: 'auto 0' }}>
            <Checkbox
              name="terms"
              id="terms"
              checked={signUpForm.terms}
              onChange={handleCheck}
            />
            I agree to the{' '}
            <Link
              href="https://ocdfeat.com/terms/"
              target="_blank"
              variant="body2"
              style={{ fontSize: '1rem' }}
            >
              terms
            </Link>{' '}
            and{' '}
            <Link
              href="https://ocdfeat.com/privacy-policy/"
              target="_blank"
              variant="body2"
              style={{ fontSize: '1rem' }}
            >
              privacy policy
            </Link>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            disableElevation
            variant="contained"
            style={{
              padding: '10px 30px 10px 30px',
              color: '#010d0d',
              border: 'solid 2px #010d0d',
              borderRadius: 30,
              fontSize: '20px',
              backgroundColor: 'transparent'
            }}
            onClick={submitForm}
          >
            Create Account
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}
